import { FormattedMessage } from 'react-intl';

import Button from '../Button/Button';
import { SectionTitleStyled } from './TermOfUse.styled';

type TermOfUsePropsT = {
  onClickPrivacyPolicy: () => void;
};

const TermOfUse: React.FC<TermOfUsePropsT> = ({ onClickPrivacyPolicy }) => {
  return (
    <>
      <SectionTitleStyled level={2}>
        <FormattedMessage id="termOfUse.title" defaultMessage="Wayaj.com Terms of Use" />
      </SectionTitleStyled>

      <SectionTitleStyled>
        1. <FormattedMessage id="termOfUse.section1.title" defaultMessage="General" />
      </SectionTitleStyled>

      <p>
        <FormattedMessage
          id="termOfUse.section1.paragraph1"
          defaultMessage="IF YOU ARE AN EUROPEAN USER PLEASE <a>CLICK HERE (GDPR)</a>"
          values={{
            a: chunk => (
              <a href="https://gdpr.eu/tag/gdpr/" target="_blank" rel="noreferrer">
                {chunk}
              </a>
            ),
          }}
        />
      </p>

      <p>
        <FormattedMessage
          id="termOfUse.section1.paragraph2"
          defaultMessage="Welcome to Wayaj.travel, which is owned and operated by Wayaj, Inc. (”us”, “we” or “our”). Any person accessing or using Wayaj is referred to as “you”. PLEASE READ THESE TERMS OF USE CAREFULLY. THESE TERMS OF USE APPLY TO YOU AND YOUR ACCESS TO, AND USE OF, THE WAYAJ WEBSITE LOCATED AT WWW.WAYAJ.TRAVEL (“SITE”) AND AS APPLICABLE, THE WAYAJ MOBILE APPLICATION (“APP”) (COLLECTIVELY, “WAYAJ”). BY ACCESSING OR USING THE SITE AND/OR APP, YOU AGREE TO BE BOUND BY THE TERMS DESCRIBED HEREIN AND ALL TERMS INCORPORATED BY REFERENCE. WE RESERVE THE RIGHT TO REVISE THESE TERMS AT ANY TIME IN OUR DISCRETION WITHOUT PRIOR NOTICE TO YOU. WHEN WE MAKE REVISIONS, WE WILL POST THEM ON WAYAJ AND THEY WILL BE EFFECTIVE IMMEDIATELY UPON POSTING AND WILL SUPERSEDE ALL PRIOR VERSIONS OF THESE TERMS. YOUR CONTINUED USE OF WAYAJ AFTER THE POSTING OF ANY REVISIONS SHALL CONFIRM YOUR ACCEPTANCE OF THE MODIFIED TERMS OF USE; THEREFORE, YOU SHOULD PERIODICALLY REVIEW THESE TERMS OF USE TO BE AWARE OF ANY CHANGES. IF YOU DO NOT AGREE TO ALL OF THESE TERMS, PLEASE DO NOT USE THE SITE OR APP."
        />
      </p>

      <SectionTitleStyled>
        2. <FormattedMessage id="termOfUse.section2.title" defaultMessage="PRIVACY" />
      </SectionTitleStyled>

      <p>
        <FormattedMessage
          id="termOfUse.section2.paragraph1"
          defaultMessage="Please take a few moments to carefully review our Privacy Policy, which describes the collection, use and disclosure of personal information from visitors to and users of Wayaj. By accessing or using Wayaj, you consent to all actions that we take with respect to your data consistent with our <a>Wayaj.com Privacy Policy</a>."
          values={{
            a: chunk => (
              <Button modifier="link" onClick={onClickPrivacyPolicy}>
                {chunk}
              </Button>
            ),
          }}
        />
      </p>

      <SectionTitleStyled>
        3. <FormattedMessage id="termOfUse.section3.title" defaultMessage="YOUR ACCOUNT" />
      </SectionTitleStyled>

      <p>
        <FormattedMessage
          id="termOfUse.section3.paragraph1"
          defaultMessage="You may create an account with Wayaj and connect to the App through your Facebook
                account. When you create an account with Wayaj, you will be required to create a user
                profile. Your user profile may include a short bio and personal travel-related
                information such as countries that you have visited and a bucket list to allow us to
                better offer targeted marketing and travel properties on the App landing page when you
                sign into your account. Your user profile may also include preferences on offsetting the
                carbon footprint of your trip. When you connect to the App using your Facebook account,
                you will give us access to certain of your Facebook account information, including your
                name, user ID, email address, birth date, gender, profile photo and public profile
                information. You are responsible for maintaining the confidentiality of your App and/or
                Facebook password(s) and restricting access to your password(s) and account. By creating
                an account and/or connecting via Facebook, you further agree to accept responsibility
                for all activities that occur under your account and agree to notify us immediately of
                any unauthorized use of your account. We reserve the right to close your account with
                Wayaj at any time for any or no reason."
        />
      </p>
      <p>
        <FormattedMessage
          id="termOfUse.section3.paragraph2"
          defaultMessage="Your Wayaj account is for your personal, non-commercial use only. In creating it, we ask
                that you provide complete and accurate information about yourself to bolster your
                credibility as a contributor to Wayaj. You are prohibited from: impersonating someone
                else; creating or using an account for anyone other than yourself; providing any email
                address or other contact information other than your own; or creating multiple accounts."
        />
      </p>

      <SectionTitleStyled>
        4.{' '}
        <FormattedMessage
          id="termOfUse.section4.title"
          defaultMessage="INTELLECTUAL PROPERTY RIGHTS; CONTENT"
        />
      </SectionTitleStyled>

      <p>
        <FormattedMessage
          id="termOfUse.section4.paragraph1"
          defaultMessage="Unless otherwise noted, Wayaj, its features, functionality, and all materials,
                including, but not limited to, images, text, graphics, logos, descriptions, data,
                illustrations, designs, icons, photographs, video clips, audio clips, sounds, files,
                trademarks, trade dress, software, specifications, advertisements, titles, names,
                written materials, and any other materials that appear as part of or on Wayaj
                (collectively, the “Content”), are protected by copyright, trade dress, and/or trademark
                laws of the United States, and all worldwide rights, title and interest in and to Wayaj
                and Content are owned, controlled and/or licensed by us. Please note that certain
                trademarks, product names, company names, logos, service marks or material displayed on
                Wayaj may be owned by third parties, and you understand that you are not authorized to
                display or use such without the prior written permission of the relevant third party."
        />
      </p>
      <p>
        <FormattedMessage
          id="termOfUse.section4.paragraph2"
          defaultMessage="Wayaj and all related names, logos, product and service names, designs and slogans are
                owned by Wayaj, Inc. or its subsidiaries, affiliates or licensors, and cannot be used
                without our prior written permission."
        />
      </p>
      <p>
        <FormattedMessage
          id="termOfUse.section4.paragraph3"
          defaultMessage="Except as may be expressly permitted by us, you may not use, reproduce, copy, publish,
                transmit, distribute, display, modify, perform, download, license, create derivative
                works of, reverse engineer, frame, transfer, sell or participate in any sale of, or
                exploit in any way, in whole or in part, any Content, Wayaj, or any related software.
                You are hereby granted a non-exclusive, limited and revocable license to view the
                Content on Wayaj, but only while accessing Wayaj."
        />
      </p>
      <p>
        <FormattedMessage
          id="termOfUse.section4.paragraph4"
          defaultMessage="While we attempt to be as accurate as possible, the Content may contain omissions,
                errors, or may be out of date. We do not represent or warrant that hotel descriptions,
                photographs, pricing or other Content is accurate, complete, current or error-free. We
                may, in our sole discretion, change, delete, update, modify or otherwise alter the
                Content at any time without providing notice to you."
        />
      </p>

      <SectionTitleStyled>
        5.{' '}
        <FormattedMessage
          id="termOfUse.section5.title"
          defaultMessage="ACCESS TO AND USE OF SITE; ACCEPTABLE USE POLICY; ACCOUNT SECURITY"
        />
      </SectionTitleStyled>

      <p>
        <FormattedMessage
          id="termOfUse.section5.paragraph1"
          defaultMessage=" We do not guarantee that Wayaj, or any Content on it, will always be available or
                uninterrupted. Access to Wayaj is permitted on a temporary basis. We may suspend,
                withdraw, discontinue, or change any or all part of Wayaj without notice. We will not be
                liable to you if, for any reason, the Site or App is unavailable at any time or for any
                period."
        />
      </p>
      <p>
        <FormattedMessage
          id="termOfUse.section5.paragraph2"
          defaultMessage="Although we make reasonable efforts to update the information on Wayaj, we make no
                representations, warranties or guarantees, whether express or implied, that the content
                on Wayaj is accurate, complete, or up to date."
        />
      </p>

      <SectionTitleStyled>
        <FormattedMessage
          id="termOfUse.section5.subTitle1"
          defaultMessage="ACCEPTABLE USE POLICY"
        />
      </SectionTitleStyled>

      <p>
        <FormattedMessage
          id="termOfUse.section5.paragraph3"
          defaultMessage="By using Wayaj, you agree to comply with all content and conduct standards set forth in
                these Terms of Use. By way of example (and not by limitation), you agree not to:"
        />
      </p>
      <p>
        <FormattedMessage
          id="termOfUse.section5.paragraph4"
          defaultMessage="a. modify, copy, distribute, transmit, display, perform, reproduce, publish, license,
                create derivative works of, frame, use, transfer or sell any information, products,
                services or Content provided through or obtained from Wayaj, including without
                limitation, engage in the practice of “screen scraping,” or any other similar activity;"
        />
      </p>
      <p>
        <FormattedMessage
          id="termOfUse.section5.paragraph5"
          defaultMessage="b. hack into Wayaj, the server on which Wayaj is stored, or any server, computer or
                database connected to Wayaj, or attack Wayaj via a denial-of-service attack or a
                distributed denial-of service attack;"
        />
      </p>
      <p>
        <FormattedMessage
          id="termOfUse.section5.paragraph6"
          defaultMessage="c. modify another Site or App so as to falsely imply that it is associated or affiliated
                with Wayaj;"
        />
      </p>
      <p>
        <FormattedMessage
          id="termOfUse.section5.paragraph7"
          defaultMessage="d. damage, disable, overburden, transmit any worms, viruses, trojans, logic bombs or
                other material of a destructive nature, or interfere with any other party’s use and
                enjoyment of Wayaj;"
        />
      </p>
      <p>
        <FormattedMessage
          id="termOfUse.section5.paragraph8"
          defaultMessage="e. violate any applicable laws, rules or regulations in your jurisdiction;"
        />
      </p>
      <p>
        <FormattedMessage
          id="termOfUse.section5.paragraph9"
          defaultMessage="f. use any automatic or manual conduct, device, process, software, program, algorithm,
                methodology or routine, including but not limited to a “robot,” “spider” or other
                similar process or functionality to interfere (or attempt to interfere) with, damage,
                disable or impose an unreasonable burden or load on, the operation of Wayaj; or"
        />
      </p>
      <p>
        <FormattedMessage
          id="termOfUse.section5.paragraph10"
          defaultMessage="g. use Wayaj for any illegal, obscene, abusive, offensive, harassing, improper or
                objectionable purpose, to sell or offer to sell any goods or services, to conduct or
                forward surveys, contests, or chain letters, or for any purpose that is prohibited by
                these Terms of Use."
        />
      </p>

      <SectionTitleStyled>
        <FormattedMessage id="termOfUse.section5.subTitle2" defaultMessage="ACCOUNT SECURITY" />
      </SectionTitleStyled>

      <p>
        <FormattedMessage
          id="termOfUse.section5.paragraph11"
          defaultMessage="If you create an account with Wayaj which requires a username and/or password, you are
                responsible for all use of your account (under any username or password) and for
                ensuring that all use of your account complies fully with the provisions of these Terms
                of Use. You shall be responsible for protecting the confidentiality of your username(s)
                or password(s), if any."
        />
      </p>

      <SectionTitleStyled>
        6.{' '}
        <FormattedMessage
          id="termOfUse.section6.title"
          defaultMessage="DMCA – COPYRIGHT COMPLAINT POLICY; INFRINGEMENT NOTIFICATION"
        />
      </SectionTitleStyled>

      <p>
        <FormattedMessage
          id="termOfUse.section6.paragraph1"
          defaultMessage="We respect the intellectual property rights of others and require that the people who
                use Wayaj do the same. If you believe that your work has been copied in a way that
                constitutes copyright infringement, please forward the following information to our
                Copyright Agent, designated as such pursuant to the Digital Millennium Copyright Act, 17
                U.S.C. §512(c)(2), named below:"
        />
      </p>

      <ul>
        <li>
          <FormattedMessage
            id="termOfUse.section6.listItem1"
            defaultMessage="Your address, telephone number, and email address;"
          />
        </li>
        <li>
          <FormattedMessage
            id="termOfUse.section6.listItem2"
            defaultMessage="A description of the copyrighted work that you claim has been infringed;"
          />
        </li>
        <li>
          <FormattedMessage
            id="termOfUse.section6.listItem3"
            defaultMessage="A description of where the alleged infringing material is located;"
          />
        </li>
        <li>
          <FormattedMessage
            id="termOfUse.section6.listItem4"
            defaultMessage="A statement by you that you have a good faith belief that the disputed use is not
                  authorized by the copyright owner, its agent, or the law;"
          />
        </li>
        <li>
          <FormattedMessage
            id="termOfUse.section6.listItem5"
            defaultMessage="An electronic or physical signature of the person authorized to act on behalf of the
                  owner of the copyright interest; and"
          />
        </li>
        <li>
          <FormattedMessage
            id="termOfUse.section6.listItem6"
            defaultMessage="A statement by you, made under penalty of perjury, that the above information in your
                  notice is accurate and that you are the copyright owner or are authorized to act on
                  the copyright owner’s behalf."
          />
        </li>
      </ul>

      <p>
        <FormattedMessage
          id="termOfUse.section6.paragraph2"
          defaultMessage="Notices of claims of copyright infringement on Wayaj should be emailed or mailed to our
                designated Copyright Agent as follows:"
        />
      </p>
      <p>
        <FormattedMessage
          id="termOfUse.section6.paragraph3"
          defaultMessage="info(@)Wayaj.travel with the subject “Copyright Infringement Notice”."
        />
      </p>
      <p>
        <FormattedMessage
          id="termOfUse.section6.paragraph4"
          defaultMessage="We encourage you to consult an attorney before filing a notice or counter-notice. Also,
                please note that you may be liable for damages (including, but not limited to, costs and
                attorneys’ fees) if you make a false claim of copyright infringement."
        />
      </p>
      <p>
        <FormattedMessage
          id="termOfUse.section6.paragraph5"
          defaultMessage="We will review and address all notices that comply with the requirements above and
                applicable law, including but not limited to the Digital Millennium Copyright Act of
                1998."
        />
      </p>

      <SectionTitleStyled>
        <FormattedMessage id="termOfUse.section6.subTitle1" defaultMessage="REPEAT INFRINGERS" />
      </SectionTitleStyled>

      <p>
        <FormattedMessage
          id="termOfUse.section6.paragraph6"
          defaultMessage="In accordance with applicable law, we have adopted a policy of terminating, in
                appropriate circumstances and at our sole discretion, subscribers or account holders who
                are deemed to be repeat copyright infringers. We may also limit or restrict access to
                Wayaj and/or terminate the accounts of any users who are deemed by us to infringe any
                intellectual property rights of others, whether or not there is any repeat infringement,
                in our sole discretion."
        />
      </p>

      <SectionTitleStyled>
        7. <FormattedMessage id="termOfUse.section7.title" defaultMessage="INDEMNIFICATION" />
      </SectionTitleStyled>

      <p>
        <FormattedMessage
          id="termOfUse.section7.paragraph1"
          defaultMessage="You agree to defend, indemnify and hold harmless Wayaj, Inc., its subsidiaries and
                affiliates and their respective members, officers, directors, employees, agents,
                licensors, suppliers, vendors, manufacturers and distributors (collectively, the “Wayaj
                Parties”) from and against any and all claims, actions, demands, charges, complaints,
                damages, losses, liabilities, costs and expenses (including but not limited to
                attorneys’ fees), arising from or in connection with: (a) your use of Wayaj; (b) any
                Content submitted, posted, or otherwise provided by you to Wayaj, any of the Wayaj
                Parties, and/or their agents, third party licensors or licensees, (c) your breach of any
                provision of these Terms of Use or any representations you provide herein, and/or (d)
                your use of Wayaj or purchase of any products or services from Wayaj. We reserve the
                right to take exclusive control and defense of any claim subject to this indemnification
                by you, in which event you will cooperate fully with us in asserting any and all
                available defenses."
        />
      </p>

      <SectionTitleStyled>
        8.{' '}
        <FormattedMessage id="termOfUse.section8.title" defaultMessage="DISCLAIMER; WARRANTIES" />
      </SectionTitleStyled>

      <p>
        <FormattedMessage
          id="termOfUse.section8.paragraph1"
          defaultMessage="EXCEPT AS EXPRESSLY SET FORTH IN THESE TERMS OF USE, YOU EXPRESSLY UNDERSTAND AND AGREE
                THAT WAYAJ, CONTENT, PRODUCTS AND/OR SERVICES ON WAYAJ ARE PROVIDED “AS IS” AND ON AN
                “AS AVAILABLE” BASIS. TO THE FULLEST EXTENT PERMITTED BY LAW AND EXCEPT AS EXPRESSLY SET
                FORTH IN THESE TERMS OF USE, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, STATUTORY
                OR OTHERWISE, INCLUDING, BUT NOT LIMITED TO, IMPLIED WARRANTIES OF MERCHANTABILITY,
                FITNESS FOR A PARTICULAR PURPOSE AND NON-INFRINGEMENT. WE DO NOT REPRESENT OR WARRANT
                THAT WAYAJ WILL BE UNINTERRUPTED OR ERROR-FREE, THAT ANY DEFECTS WILL BE CORRECTED, OR
                THAT WAYAJ OR THE SERVER THAT MAKES WAYAJ AVAILABLE ARE FREE FROM VIRUSES OR ANYTHING
                ELSE HARMFUL. FURTHER, EXCEPT AS EXPRESSLY SET FORTH IN THESE TERMS OF USE, WE MAKE NO
                WARRANTIES OR REPRESENTATIONS ABOUT THE ACCURACY, ADEQUACY, USEFULNESS, RELIABILITY, OR
                COMPLETENESS OF WAYAJ, PRODUCTS, SERVICES, CONTENT, THE CONTENT OF ANY THIRD-PARTY SITE
                LINKED TO OR FROM WAYAJ, USER GENERATED CONTENT, SUBMISSIONS, INFORMATION, INFORMATION
                PROVIDED BY THIRD PARTY VENDORS, OR ANY OTHER ITEMS OR MATERIALS ON WAYAJ OR LINKED TO
                FROM WAYAJ."
        />
      </p>
      <p>
        <FormattedMessage
          id="termOfUse.section8.paragraph2"
          defaultMessage="WE ASSUME NO LIABILITY OR RESPONSIBILITY FOR ANY: (A) ERRORS, MISTAKES OR INACCURACIES
                OF THE CONTENT, PRODUCTS, SERVICES, INFORMATION, SITE AND MATERIALS SET FORTH ON OR MADE
                AVAILABLE THROUGH WAYAJ, (B) PERSONAL INJURY OR PROPERTY DAMAGE, OF ANY NATURE
                WHATSOEVER, RESULTING FROM YOUR ACCESS TO OR USE OF WAYAJ, PRODUCTS, SERVICES OR ANY
                THIRD PARTY SITE(S), PRODUCTS OR SERVICES, (C) ANY UNAUTHORIZED ACCESS TO OR USE OF THE
                SERVERS THAT HOST WAYAJ OR ANY THIRD PARTY SITE(S) AND/OR ANY AND ALL PERSONAL
                INFORMATION STORED THEREIN, (D) ANY INTERRUPTION OR CESSATION OF TRANSMISSION TO OR FROM
                WAYAJ OR THIRD PARTY SITE(S), (E) ANY BUGS, VIRUSES, TROJAN HORSES OR THE LIKE, WHICH
                MAY BE TRANSMITTED TO OR THROUGH WAYAJ OR ANY THIRD PARTY SITE(S) BY WAYAJ OR ANY THIRD
                PARTY, AND/OR (F) ANY ERRORS OR OMISSIONS IN THE NETWORK OR ANY CONTENT, INFORMATION AND
                MATERIALS (INCLUDING BUT NOT LIMITED TO THIRD PARTY SITE(S)) OR FOR ANY LOSS OR DAMAGE
                OF ANY KIND INCURRED AS A RESULT OF THE USE OF ANY OF THE FOREGOING."
        />
      </p>
      <p>
        <FormattedMessage
          id="termOfUse.section8.paragraph3"
          defaultMessage="NO PERSON (INCLUDING ANY AGENT, DEALER OR REPRESENTATIVE OF WAYAJ) IS AUTHORIZED TO MAKE
                ANY REPRESENTATION OR WARRANTY CONCERNING PRODUCTS, AND YOU ACKNOWLEDGE AND AGREE THAT
                YOU HAVE NOT RELIED ON ANY OTHER WARRANTIES OR REPRESENTATIONS."
        />
      </p>

      <SectionTitleStyled>
        9.{' '}
        <FormattedMessage id="termOfUse.section9.title" defaultMessage="LIMITATION OF LIABILITY" />
      </SectionTitleStyled>

      <p>
        <FormattedMessage
          id="termOfUse.section9.paragraph1"
          defaultMessage="IN NO EVENT SHALL Wayaj, Inc., OR ITS SUBSIDIARIES, AFFILIATES, AGENTS, SUPPLIERS, OR
                VENDORS (COLLECTIVELY, THE “WAYAJ RELEASED PARTIES”) BE LIABLE FOR ANY INDIRECT,
                SPECIAL, PUNITIVE, INCIDENTAL, EXEMPLARY OR CONSEQUENTIAL DAMAGES, INCLUDING, WITHOUT
                LIMITATION, DAMAGES FOR LOSS OF USE, DATA, REVENUE OR PROFITS, BUSINESS INTERRUPTION, OR
                LOSS OF BUSINESS OPPORTUNITY OR GOODWILL, ARISING FROM OR IN CONNECTION WITH (A) THE USE
                OF, OR INABILITY TO USE, WAYAJ; (B) THE PROVISION OF OR FAILURE TO PROVIDE SERVICES,
                PRODUCTS, MATERIALS, CONTENT, OR SOFTWARE AVAILABLE FROM, ON OR THROUGH WAYAJ OR ANY
                THIRD-PARTY SITE(S); OR (C) THE CONDUCT OF OTHER USERS OF WAYAJ, WHETHER BASED ON
                CONTRACT, TORT, NEGLIGENCE, STRICT LIABILITY OR OTHERWISE, EVEN IF SUCH PARTY HAS BEEN
                ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. YOU ASSUME COMPLETE RESPONSIBILITY FOR YOUR
                USE OF WAYAJ. YOUR SOLE REMEDY AGAINST THE WAYAJ RELEASED PARTIES FOR DISSATISFACTION
                WITH WAYAJ OR ANY CONTENT IS TO STOP USING WAYAJ. THAT SAID, IF ANY OF THE WAYAJ
                RELEASED PARTIES ARE FOUND TO BE LIABLE TO YOU FOR ANY DAMAGE OR LOSS ARISING OUT OF OR
                WHICH IS IN ANY WAY CONNECTED WITH YOUR USE OF WAYAJ, ANY CONTENT, OR PURCHASE OF ANY
                PRODUCTS OR SERVICES ON OR THROUGH WAYAJ, THE WAYAJ RELEASED PARTIES’ TOTAL LIABILITY
                SHALL NOT EXCEED ONE HUNDRED DOLLARS ($100.00) IN THE AGGREGATE."
        />
      </p>
      <p>
        <FormattedMessage
          id="termOfUse.section9.paragraph2"
          defaultMessage="SOME JURISDICTIONS DO NOT ALLOW THE LIMITATION OR EXCLUSION OF CERTAIN WARRANTIES, OR
                THE EXCLUSION OR LIMITATION OF CERTAIN DAMAGES. IF YOU RESIDE IN ONE OF THESE
                JURISDICTIONS, THE ABOVE LIMITATIONS OR EXCLUSIONS NOTED IN SECTIONS 8 AND 9 MAY NOT
                APPLY TO YOU."
        />
      </p>

      <SectionTitleStyled>
        10.{' '}
        <FormattedMessage
          id="termOfUse.section10.title"
          defaultMessage="THIRD PARTY VENDORS/SUPPLIERS"
        />
      </SectionTitleStyled>

      <p>
        <FormattedMessage
          id="termOfUse.section10.paragraph1"
          defaultMessage="Please note that if Wayaj has any third party products or services on Wayaj, these
                products/services are provided by third party vendors/suppliers and not by Wayaj. As set
                forth in Sections 8 and 9 above, we are not responsible for any third party
                products/services or for the actions or inactions of any third party vendors or
                suppliers. While we aim to ensure that all third party vendors and suppliers are honest
                and truthful when making representations regarding their products/services and/or any
                causes they claim to support and how, we cannot warrant or represent that the statements
                they make are truthful, complete or accurate. Please contact the relevant third party
                vendor or supplier if you have further questions regarding their products or services."
        />
      </p>

      <SectionTitleStyled>
        11. <FormattedMessage id="termOfUse.section11.title" defaultMessage="PROMOTIONS" />
      </SectionTitleStyled>

      <p>
        <FormattedMessage
          id="termOfUse.section11.paragraph1"
          defaultMessage="Any and all offers or promotions advertised on Wayaj are void where prohibited, and are
                subject to the official rules or terms and conditions of any such offers or promotions."
        />
      </p>

      <SectionTitleStyled>
        12.{' '}
        <FormattedMessage
          id="termOfUse.section12.title"
          defaultMessage="GENERAL LEGAL PROVISIONS"
        />
      </SectionTitleStyled>

      <p>
        <FormattedMessage
          id="termOfUse.section12.paragraph1"
          defaultMessage="These Terms of Use are effective unless and until terminated by either you or us. You
                may terminate these Terms of Use as they apply to you at any time by ceasing to use
                Wayaj. We may terminate these Terms of Use at any time immediately and without notice,
                and accordingly deny you access to Wayaj, for any reason in our sole discretion;
                however, the provisions in these Terms of Use that relate to intellectual property,
                indemnification, disclaimer, limitation of liability, and choice of law shall survive
                any termination of these Terms of Use."
        />
      </p>
      <p>
        <FormattedMessage
          id="termOfUse.section12.paragraph2"
          defaultMessage="These Terms of Use and your use of Wayaj shall be governed and construed in accordance
                with the laws of the State of New York, without regard to any conflict of law
                provisions. Any dispute arising under these Terms of Use shall be resolved exclusively
                by the state and/or federal courts of the State of New York, County of Nassau and/or the
                Eastern District of New York. By using Wayaj, you hereby irrevocably and unconditionally
                consent and submit to the exclusive jurisdiction of such courts over any suit, action or
                proceeding arising out of these Terms of Use and/or your access to or use of the Site
                and/or App and/or your purchase of products or services on or through Wayaj, and you
                waive any claim of forum non convenience or lack of personal jurisdiction that you may
                have."
        />
      </p>
      <p>
        <FormattedMessage
          id="termOfUse.section12.paragraph3"
          defaultMessage="If any portion of these Terms of Use is held to be invalid or unenforceable, the invalid
                or unenforceable portion shall be modified in accordance with the applicable law with a
                provision that most closely reflects the intention of the original provision, and the
                remainder of these Terms of Use shall remain in full force and effect. Our failure to
                insist upon or enforce strict performance by you of any provision of these Terms of Use
                shall not be construed as a waiver of any provision or right."
        />
      </p>
      <p>
        <FormattedMessage
          id="termOfUse.section12.paragraph4"
          defaultMessage="We may provide notice to you relating to Wayaj and/or these Terms of Use by sending an
                email to your last known email address, and any such notice shall be deemed given and
                received on the day it is sent. A printed version of these Terms of Use and of any
                notices given to you in electronic form or otherwise shall be admissible in judicial or
                administrative proceedings based upon or relating to these Terms of Use to the same
                extent and subject to the same conditions as other business documents and records
                originally generated and maintained in printed form. You agree that any cause of action
                that you may desire to bring arising out of or related to these Terms of Use and/or
                Wayaj must commence within one (1) year after the cause of action arises. You may not
                use Wayaj or export the Content in violation of U.S. export laws and regulations. If you
                access Wayaj from a location outside the United States, you are responsible for
                compliance with all local laws."
        />
      </p>
      <p>
        <FormattedMessage
          id="termOfUse.section12.paragraph5"
          defaultMessage="Our performance of these Terms of Use is subject to existing laws and legal process, and
                nothing contained in these Terms of Use is in derogation of our right to comply with law
                enforcement requests or requirements."
        />
      </p>
      <p>
        <FormattedMessage
          id="termOfUse.section12.paragraph6"
          defaultMessage="These Terms of Use (together with our <a>Wayaj.com Privacy Policy</a>
                , which is expressly incorporated herein by reference and which can be accessed on
                Wayaj, and any other terms that may appear on Wayaj from time-to-time) contain the
                entire understanding between you and us with respect to your use of, and access to,
                Wayaj, and supersede all prior agreements, terms, conditions and understandings, both
                written and oral, with respect to such use of, and access to, Wayaj. No representation,
                statement or inducement, whether oral or written, not contained in these Terms of Use
                (and any other terms that may appear on Wayaj from time-to-time) or the{' '}
                <a>Wayaj.com Privacy Policy</a> shall bind any party to this agreement. No additional or different terms or conditions
                will be binding upon us unless expressly agreed to in writing by an officer of Wayaj,
                Inc.. No other representative has any authority to waive, alter, vary or add to these
                Terms of Use. Before using Wayaj, please read through all referenced documents
                carefully."
          values={{
            a: chunk => (
              <Button modifier="link" onClick={onClickPrivacyPolicy}>
                {chunk}
              </Button>
            ),
          }}
        />
      </p>

      <SectionTitleStyled>
        13. <FormattedMessage id="termOfUse.section13.title" defaultMessage="COPYRIGHT" />
      </SectionTitleStyled>

      <p>
        <FormattedMessage
          id="termOfUse.section13.paragraph1"
          defaultMessage="© 2017-2019 Wayaj, Inc. All rights reserved."
        />
      </p>
    </>
  );
};

export default TermOfUse;
